import React from "react";
import { ColumnsType } from "antd/es/table";
import type { SortOrder } from "antd/es/table/interface";
import { TFunction } from "i18next";
import { nanoid } from "nanoid";

import Warning from "~/components/Icons/alert-square";
import Violation from "~/components/Icons/icon-violations";
import { IconMimeType } from "~/libs/resolveMemeType";
import { InfoTooltip } from "~/shared/ui/Tooltip";

import s from "~/components/Table/TableRow/DefaultTableRow/TableRow.less";


export const ANNOUNCEMENT_SORTABLE_COLUMNS = [
  "title",
  "createdByUser",
  "date",
  "singedBy",
  "status",
  "createdAt",
  "importance",
];

const localeCompare = (a: unknown, b: unknown) =>
  String(a ?? "").localeCompare(String(b ?? ""));

const COMPARATORS: Record<string, (a: any, b: any) => number> = {
  title: (a, b) => localeCompare(a.title, b.title),
  createdByUser: (a, b) => localeCompare(a.createdByUser, b.createdByUser),
  date: (a, b) => (a.dateRaw ?? 0) - (b.dateRaw ?? 0),
  singedBy: (a, b) => localeCompare(a.singedBy, b.singedBy),
  status: (a, b) => localeCompare(a.status, b.status),
  createdAt: (a, b) => (a.createdAtRaw ?? 0) - (b.createdAtRaw ?? 0),
  importance: (a, b) =>
    Number(a.importance === "urgent") - Number(b.importance === "urgent"),
};

export const getAnnouncementColumns = (
  t: TFunction,
  options: string[],
  sortField?: string,
  sortType?: string,
): ColumnsType<any> =>
  options.map((option) => {
    const comparator = COMPARATORS[option];
    const sortOrder: SortOrder | undefined =
      comparator &&
      sortField === option &&
      (sortType === "ASC" || sortType === "DESC")
        ? sortType === "DESC"
          ? "descend"
          : "ascend"
        : undefined;

    const baseColumn = {
      title: t(`announcement.${option}`),
      dataIndex: option,
      key: option,
      ...(comparator ? { sorter: comparator, sortOrder } : {}),
    };

    // Special render for Documents field - show file links in grid
    if (option === "Documents") {
      return {
        ...baseColumn,
        render: (value: any) => {
          if (!value || !Array.isArray(value)) return null;
          return (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: "2px",
              }}
            >
              {value.map((file) => (
                <span key={nanoid()} className={s.cont}>
                  {!!file?.link && (
                    <a
                      href={file.link}
                      download
                      onClick={(e) => e.stopPropagation()}
                      target="_blank"
                      placeholder={file.name} rel="noreferrer"
                    >
                      {IconMimeType(file)}
                    </a>
                  )}
                </span>
              ))}
            </div>
          );
        },
      };
    }

    // Special render for title field - show with tooltip on hover
    if (option === "title") {
      return {
        ...baseColumn,
        render: (value: any, record: any) => (
          <div style={{ display: "flex", alignItems: "center" }}>
            <div className={s.elementEffect}>{value}</div>
            <InfoTooltip content={record.text} />
          </div>
        ),
      };
    }

    // Special render for importance field - show icons
    if (option === "importance") {
      return {
        ...baseColumn,
        render: (value: any) =>
          value === "urgent" ? (
            <Violation
              fill={"#db0505"}
              color={"#fff"}
              width={"22"}
              height={"22"}
            />
          ) : (
            <span className={s.svgImg}>
              <Warning />
            </span>
          ),
      };
    }

    return {
      ...baseColumn,
      render: (value: any) => <span>{value}</span>,
    };
  });
